// @flow
import flatten from 'lodash/fp/flatten';
import map from 'lodash/fp/map';
import pipe from 'lodash/fp/pipe';
import last from 'lodash/fp/last';
import lodashSortBy from 'lodash/fp/sortBy';
import uniqBy from 'lodash/fp/uniqBy';

import { byNumber, type Customer } from './customer';
import type { Catalog } from './catalog';
import {
  type AbstractCatalog,
  byKey,
  byLive,
  allLive,
  byPermissions,
  dealerAtsLabelMax,
  repAtsLabelMax,
} from './abstract-catalog';
import type { CustomCatalog, CustomCatalogAsCatalog } from './custom-catalog';
import type { ExportAvailability } from '../ui/settings-context';
import type { Dashboard } from './dashboard';
import type { Voucher } from './vouchers';
import type { ROE } from './type';

import { topLevelPathsCollapsed } from 'modules/tag-tree';
import { type Facet } from 'modules/facet';

export type Group = {
  customers: Customer[],
};

export type UserType = 'rep' | 'dealer';

// https://github.com/ElasticSuite/skillet/blob/develop/app/controllers/users_controller.rb#L70-L80
export type PartialUser = $ReadOnly<{
  id: string,
  name: string,
  customerNumber: string,
  region: string,
  type: UserType,
  email: string,
  // catalogPermissions: any,
}>;

// https://github.com/ElasticSuite/skillet/blob/develop/app/controllers/users_controller.rb#L108-L122
export type ManagedUser = $ReadOnly<{
  id: string,
  externalId: string,
  name: string,
  type: UserType,
  email: string,
  customerNumber: string,
  username: string,
  firstName: string,
  lastName: string,
  isDisabled: boolean,
  isManager: boolean,
  destroyable: boolean,
  prebookAccess: boolean,
  password?: string,
  passwordConfirmation?: string,
  brandAdmin: boolean,
  currency_code?: string,
}>;

export type Notification = $ReadOnly<{
  _id: string,
  text: string,
  type?: ?string,
  created_at: Date,
  expiration_date: string,
  published: boolean,
}>;

export type User = $ReadOnly<{
  ...ManagedUser,
  _id: string,
  password?: string,
  passwordConfirmation?: string,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
  groups?: Group[],
  catalogs: $ReadOnlyArray<Catalog>,
  customer: Customer,
  dashboard?: Dashboard,
  dashboards: Dashboard[],
  activeVouchers?: Voucher[],
  documentsDisabled?: boolean,
  notifications: Notification[] | null,
  associatedCustomers?: ?$ReadOnlyArray<string>,
  // notes: Note[],
  // ratings: Rating[],
}>;

export type SerializedManagedUser = {
  _id: string,
  external_id: string,
  customer_number: string,
  prebook_access: boolean,
  type: UserType,
  name: string,
  username: string,
  email: string,
  first_name: string,
  last_name: string,
  is_disabled: boolean,
  is_manager: boolean,
  password?: string,
  password_confirmation?: string,
  destroyable: boolean,
  brandAdmin: boolean,
};

type SerializedManagedUsers = $ReadOnly<{
  results: $ReadOnlyArray<SerializedManagedUser>,
}>;
export type SerializedUser = {
  ...SerializedManagedUser,
  document_duplicated_for?: boolean,
  custom_catalog_shared_to?: boolean,
};

export type UsersQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<SerializedUser>,
  total: number,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type SerializedUsersQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<SerializedUser>,
  range: string,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type UsersQuery = $ReadOnly<{|
  keyword: string | null,
  tagPaths: $ReadOnlyArray<$ReadOnlyArray<string>>,
  sortBy?: 'name' | 'email' | 'customer_name' | 'customer_number',
  sortDirection?: 'ASC' | 'DESC',
  rep2repsharing?: boolean,
  Range?: string,
  catalog?: AbstractCatalog,
  orderId?: string,
  customCatalogId?: string,
|}>;

export type AssociatedCustomerResponse = ROE<{
  customer_name: string,
}>;

export const customers = (user: User): $ReadOnlyArray<Customer> =>
  user.type === 'rep'
    ? pipe(
        uniqBy('number'),
        lodashSortBy('name'),
      )([
        ...pipe(
          map((group) => group.customers),
          flatten,
        )(user.groups || []),
      ])
    : [user.customer];

export const customerByNumber = (user: User, number: string): Customer | null =>
  byNumber(customers(user), number);

export const catalogByKey = (
  user: User,
  catalogKey: string,
): AbstractCatalog | null => byKey(user.catalogs, catalogKey);

export const usersOwnCustomer = (user: User): Customer | null =>
  byNumber(customers(user), user.customer.number);

export const customersWithOwnFirst = (user: User): $ReadOnlyArray<Customer> => {
  const usersCustomer = usersOwnCustomer(user);
  const userCustomers = customers(user);
  return [
    ...(usersCustomer ? [usersCustomer] : []),
    ...userCustomers.filter((c) => c !== usersCustomer),
  ];
};

export const canExportAvailabilityForCatalog = (
  exportAvailability: ExportAvailability,
  catalog: AbstractCatalog,
  user: User,
): boolean => {
  /* eslint-disable babel/no-unused-expressions */
  if (catalog.isCustomCatalog) {
    (catalog: CustomCatalogAsCatalog);
    return false;
  }
  (catalog: Catalog);
  /* eslint-enable babel/no-unused-expressions */
  return (
    (exportAvailability === 'both' ||
      (exportAvailability === true && user.type === 'rep')) &&
    catalog &&
    catalog.type !== 'nonblocking'
  );
};

export const catalogsForCustomer = (
  user: User,
  customer: Customer,
  catalogs: $ReadOnlyArray<AbstractCatalog>,
): $ReadOnlyArray<AbstractCatalog> =>
  byLive(
    byPermissions(catalogs, customer.number, user.customer.catalog_permissions),
  );

export const allLiveCatalogsForCustomer = (
  user: User,
  customer: Customer,
  catalogs: $ReadOnlyArray<Catalog>,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
  excludeCatalogs?: boolean = false,
): $ReadOnlyArray<AbstractCatalog> =>
  catalogsForCustomer(
    user,
    customer,
    allLive(catalogs, customCatalogs, excludeCatalogs),
  );

/**
 * Returns the only live base catalog for a customer if the customer has exactly one.
 */
export const singularBaseCatalog = (
  user: User,
  customer: Customer,
): AbstractCatalog | null =>
  allLiveCatalogsForCustomer(user, customer, user.catalogs, []).find(
    (_e, _idx, list) => list.length === 1,
  ) || null;

const dashboard = (user: User): Dashboard | null => user.dashboard || null;
const dashboards = (user: User): Dashboard[] => user.dashboards || [];

export const currentDashboardBrand = (user: User): string | null => {
  const currentDashboard = dashboard(user);

  return currentDashboard &&
    currentDashboard.brand &&
    currentDashboard.brand.key &&
    currentDashboard.brand.key.length > 0
    ? currentDashboard.brand.key
    : null;
};

export const currentDashboardIsMultibrand = (user: User): boolean => {
  const userDashboards = dashboards(user);
  const currentDashboard = dashboard(user);
  if (userDashboards.length > 1 && currentDashboard !== null) {
    if (currentDashboard.brand && currentDashboard.brand.key === '') {
      return true;
    }
  }
  return false;
};

export const canEmbellish = (
  user: User,
  pageProductIsEmbellishable: boolean,
  embellishmentsActionableForRepsOnly: boolean,
): boolean => {
  if (embellishmentsActionableForRepsOnly && user.type === 'dealer')
    return false;
  return pageProductIsEmbellishable;
};

export const usersQueryAsQueryString = ({
  keyword,
  tagPaths,
  sortBy,
  sortDirection,
  rep2repsharing,
  catalog,
  Range,
  orderId,
  customCatalogId,
}: UsersQuery): string => {
  const params = new URLSearchParams();

  if (keyword) params.append('keyword', keyword);

  const userTypes = ['rep', 'dealer', 'internal'];

  const queryType = userTypes.find((type) =>
    tagPaths.map((path) => path[0]).includes(type),
  );

  if (queryType) params.append('type', queryType);

  const adjustedTagPaths = topLevelPathsCollapsed(
    tagPaths.map((path) =>
      path.filter((part, idx) => idx > 0 || !userTypes.includes(part)),
    ),
  );

  adjustedTagPaths.forEach((path) => {
    const value = last(path);
    const body = path.slice(0, -1);

    if (body.length === 0) return;

    params.append(`tags[${body.join('][')}][]`, value);
  });

  if (catalog) params.append('catalog_key', catalog.key);

  if (Range) params.append('Range', Range);

  if (orderId) params.append('document_id', orderId);

  if (customCatalogId) params.append('custom_catalog_id', customCatalogId);

  params.append('rep2repsharing', rep2repsharing ? 'true' : 'false');

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const sortParam = encodeURIComponent(
    sortBy ? `sort(${sortDirectionSymbol}${sortBy})` : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export const deserializedUsersQueryResponse = ({
  results,
  facets,
  range,
}: SerializedUsersQueryResponse): UsersQueryResponse => ({
  results,
  facets,
  total: Number(last(range.split('/'))),
});

export const isShared = (user: SerializedUser): boolean =>
  Boolean(user.document_duplicated_for || user.custom_catalog_shared_to);

export const atsLabelMaxForCatalog =
  (catalog: AbstractCatalog): ((user: User) => number | null) =>
  (user: User): number | null =>
    ((user.type === 'rep' || null) && repAtsLabelMax(catalog)) ??
    dealerAtsLabelMax(catalog);

export const ordersFromCatalogAreShareableToUser = (
  userType: UserType,
  customer: Customer,
  catalog: AbstractCatalog,
): boolean => {
  const visibleTo = catalog.visible_to || null;
  // the default setting for visible_to is 'all'
  // but the value might not have been explicitly
  // set by the client through the admin page (CSP),
  // in which case the field will be null, but should
  // be interpreted as the setting 'all'
  switch (visibleTo) {
    case null:
      return true;
    case 'all':
      return true;
    case 'dealer':
      return (
        userType === 'dealer' ||
        (!!customer.prebook_access && catalog.classification === 'Prebook')
      );
    case 'rep':
      return (
        userType === 'rep' ||
        (!!customer.prebook_access && catalog.classification === 'Prebook')
      );
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (visibleTo: empty);
      throw new Error(
        `Unhandled value on catalog field visible_to: ${visibleTo}`,
      );
  }
};

export const deserializedManagedUser = (
  smu: SerializedManagedUser,
): ManagedUser => ({
  customerNumber: smu.customer_number,
  destroyable: smu.destroyable,
  email: smu.email,
  firstName: smu.first_name,
  type: smu.type,
  // eslint-disable-next-line no-underscore-dangle
  id: smu._id,
  externalId: smu.external_id,
  isDisabled: smu.is_disabled,
  isManager: smu.is_manager,
  lastName: smu.last_name,
  name: smu.name,
  username: smu.username,
  prebookAccess: smu.prebook_access,
  brandAdmin: smu.brandAdmin,
});

export const serializedManagedUser = (
  mu: ManagedUser,
): SerializedManagedUser => ({
  _id: mu.id,
  external_id: mu.externalId,
  type: mu.type,
  prebook_access: mu.prebookAccess,
  customer_number: mu.customerNumber,
  destroyable: mu.destroyable,
  email: mu.email,
  first_name: mu.firstName,
  is_disabled: mu.isDisabled,
  is_manager: mu.isManager,
  last_name: mu.lastName,
  name: mu.name,
  password: mu.password,
  password_confirmation: mu.passwordConfirmation,
  username: mu.username,
  brandAdmin: mu.brandAdmin,
});

export const deserializedManagedUsers = ({
  results,
}: SerializedManagedUsers): $ReadOnlyArray<ManagedUser> =>
  results.map((user) => deserializedManagedUser(user));

export const fullname = ({ name, firstName, lastName }: User): string =>
  name || `${firstName} ${lastName}`;

export const associatedCustomerNumbers = (user: User): $ReadOnlyArray<string> =>
  user.associatedCustomers?.filter(
    (number) => number !== user.customer.number,
  ) ?? [];
